<template>
  <div class="kw-lucy-controls">
    <template v-if="isMainKeyword">
      <v-chip
        x-small
        class="text-none kw-main-chip"
        color="grey darken-3"
        text-color="white"
      >
        {{ $t('keywords.lucy.alwaysInArticle') }}
      </v-chip>
    </template>
    <template v-else>
      <div class="kw-lucy-toggle-row d-flex align-center justify-end">
        <v-btn
          depressed
          data-test="lucy-row-toggle"
          class="text-none kw-lucy-btn"
          :class="selected ? 'kw-lucy-btn--on' : 'kw-lucy-btn--off'"
          :aria-pressed="selected ? 'true' : 'false'"
          :disabled="lucyToggleDisabled"
          @click.stop="onToggleLucy"
        >
          <v-icon
            left
            x-small
          >
            {{ selected ? 'check' : 'add' }}
          </v-icon>
          {{ $t('keywords.list.header.forAiLucy') }}
        </v-btn>
        <WorkspaceControlTip
          class="kw-lucy-tip ml-1"
          icon-only
          :text="lucyTooltip"
        />
      </div>
      <div
        v-if="selected && showIntentChoices"
        class="kw-intent-toggle mt-1 d-flex flex-wrap"
      >
        <div
          v-for="opt in intentOptions"
          :key="opt.value"
          class="kw-intent-row d-flex align-center"
        >
          <v-btn
            x-small
            class="text-none"
            :depressed="currentIntent === opt.value"
            :outlined="currentIntent !== opt.value"
            :color="'primary'"
            :disabled="readOnly"
            @click.stop="onSetIntent(opt.value)"
          >
            {{ opt.label }}
          </v-btn>
          <WorkspaceControlTip
            class="kw-lucy-tip ml-1"
            icon-only
            :text="intentTip(opt.value)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {KeywordType} from '@/enum/keyword_type';
    import type {KeywordForAi} from '@/model/Article';
    import WorkspaceControlTip from '@/components/keywords/WorkspaceControlTip.vue';
    import {
        defaultLucyIntent,
        findKeywordForAi,
        isKeywordSelectedForLucy,
        resolveLucyIntent,
        setLucyIntent,
        toggleLucyKeyword,
        type LucyKeywordIntent,
        countLucySlots,
    } from '@/service/keywordLucySelection';
    import {MAX_AI_KEYWORDS} from '@/utils/Article';
    import {looksLikeQuestionPhrase} from '@/service/questionKeywordRows';

    export default Vue.extend({
        name: 'KeywordLucyRowControls',
        components: {WorkspaceControlTip},
        props: {
            phrase: {
                type: String,
                required: true,
            },
            keywordType: {
                type: Number,
                default: KeywordType.RELATED,
            },
            isMainKeyword: {
                type: Boolean,
                default: false,
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            selectedForAi: {
                type: Array as () => KeywordForAi[],
                default: () => [],
            },
            showIntentChoices: {
                type: Boolean,
                default: true,
            },
        },
        computed: {
            selected(): boolean {
                return isKeywordSelectedForLucy(this.selectedForAi, this.phrase);
            },
            currentIntent(): LucyKeywordIntent {
                const row = findKeywordForAi(this.selectedForAi, this.phrase);
                if (!row) {
                    return defaultLucyIntent(this.keywordType, looksLikeQuestionPhrase(this.phrase));
                }
                return resolveLucyIntent(row);
            },
            slotCount(): number {
                return countLucySlots(this.selectedForAi);
            },
            intentOptions(): Array<{value: LucyKeywordIntent; label: string}> {
                const labels: Record<LucyKeywordIntent, string> = {
                    section: String(this.$t('keywords.lucy.intent.section')),
                    faq: String(this.$t('keywords.lucy.intent.faq')),
                    weave: String(this.$t('keywords.lucy.intent.weave')),
                };
                const order: LucyKeywordIntent[] = this.keywordType === KeywordType.QUESTION
                    ? ['faq', 'section', 'weave']
                    : ['section', 'faq', 'weave'];
                return order.map((value) => ({value, label: labels[value]}));
            },
            lucyTooltip(): string {
                if (this.isMainKeyword) {
                    return String(this.$t('keywords.lucy.tip.mainTopic'));
                }
                if (this.selected) {
                    return String(this.$t('keywords.lucy.tip.turnOff'));
                }
                const intent = defaultLucyIntent(this.keywordType, looksLikeQuestionPhrase(this.phrase));
                if ((intent === 'section' || intent === 'faq') && this.slotCount >= MAX_AI_KEYWORDS) {
                    return String(this.$t('keywords.lucy.tip.blockedAtLimit'));
                }
                return String(this.$t('keywords.lucy.tip.turnOn'));
            },
            lucyToggleDisabled(): boolean {
                if (this.readOnly) {
                    return true;
                }
                if (this.selected) {
                    return false;
                }
                const intent = defaultLucyIntent(this.keywordType, looksLikeQuestionPhrase(this.phrase));
                return (intent === 'section' || intent === 'faq') && this.slotCount >= MAX_AI_KEYWORDS;
            },
        },
        methods: {
            intentTip(intent: LucyKeywordIntent): string {
                if (intent === 'section') {
                    return String(this.$t('keywords.lucy.tip.intentSection'));
                }
                if (intent === 'faq') {
                    return String(this.$t('keywords.lucy.tip.intentFaq'));
                }
                return String(this.$t('keywords.lucy.tip.intentWeave'));
            },
            emitRows(rows: KeywordForAi[]): void {
                this.$emit('update:selectedForAi', rows);
            },
            onToggleLucy(): void {
                const result = toggleLucyKeyword({
                    rows: this.selectedForAi,
                    keyword: this.phrase,
                    keywordType: this.keywordType,
                    phraseIsQuestion: looksLikeQuestionPhrase(this.phrase),
                });
                if (result.blocked === 'limit') {
                    this.$store.dispatch('toasts/add', {
                        message: String(this.$t('keywords.lucy.limitBlocked')),
                        type: 'warning',
                    });
                    return;
                }
                this.emitRows(result.rows);
            },
            onSetIntent(intent: LucyKeywordIntent): void {
                const result = setLucyIntent({
                    rows: this.selectedForAi,
                    keyword: this.phrase,
                    keywordType: this.keywordType,
                    intent,
                });
                if (result.blocked === 'limit') {
                    this.$store.dispatch('toasts/add', {
                        message: String(this.$t('keywords.lucy.limitBlocked')),
                        type: 'warning',
                    });
                    return;
                }
                this.emitRows(result.rows);
            },
        },
    });
</script>

<style scoped lang="sass">
.kw-lucy-controls
    display: flex
    flex-direction: column
    align-items: flex-end
    min-width: 148px

.kw-lucy-btn
    font-size: 11px !important
    letter-spacing: .01em
    min-width: 0 !important
    padding: 0 8px !important

.kw-lucy-btn--on
    background: #e8f4fd !important
    color: var(--sw-primary-fill) !important

.kw-lucy-btn--off
    background: #fff !important
    color: var(--sw-primary-fill) !important
    border: 1px solid rgba(5, 102, 195, 0.55) !important
    box-shadow: none !important

    &:not(.v-btn--disabled):hover
        background: #e8f4fd !important

.kw-intent-toggle
    gap: 4px
    justify-content: flex-end

.kw-intent-row
    gap: 2px

.kw-lucy-tip
    flex-shrink: 0

.kw-main-chip
    font-size: 10px !important
</style>
